import React, { useState, useEffect, useCallback } from 'react';
import useStore from '../store/useStore';
import { fetchJavaVersions, fetchSpringBootVersions } from '../data/fetchVersions';
import { API_BASE } from '../api';
import AuthModal from './AuthModal';
import { useT } from '../i18n';
import Logo from './Logo';

function Section({ title, icon, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="menu-section">
      <div className="menu-section-header" onClick={() => setOpen(!open)}>
        <span className="menu-section-icon">{icon}</span>
        <span className="menu-section-title-text">{title}</span>
        <span className={`menu-chevron ${open ? 'open' : ''}`}>&#9654;</span>
      </div>
      {open && <div className="menu-section-body">{children}</div>}
    </div>
  );
}

export default function Toolbar({ collapsed, onToggle }) {
  const t = useT();
  const theme = useStore((s) => s.theme);
  const toggleTheme = useStore((s) => s.toggleTheme);
  const language = useStore((s) => s.language);
  const setLanguage = useStore((s) => s.setLanguage);
  const set = useStore((s) => s.setProjectConfig);
  const generateJSON = useStore((s) => s.generateJSON);
  const addEntityNode = useStore((s) => s.addEntityNode);
  const addEnumNode = useStore((s) => s.addEnumNode);
  const accessToken = useStore((s) => s.accessToken);
  const logout = useStore((s) => s.logout);

  const readOnly = useStore((s) => s.readOnly);
  const templateMode = useStore((s) => s.templateMode);
  const projectName = useStore((s) => s.projectName);
  const basePackage = useStore((s) => s.basePackage);
  const buildTool = useStore((s) => s.buildTool);
  const javaVersion = useStore((s) => s.javaVersion);
  const springBootVersion = useStore((s) => s.springBootVersion);
  const packaging = useStore((s) => s.packaging);
  const database = useStore((s) => s.database);
  const dbUsername = useStore((s) => s.dbUsername);
  const dbPassword = useStore((s) => s.dbPassword);
  const features = useStore((s) => s.features);
  const toggleFeature = useStore((s) => s.toggleFeature);
  const jwtSecretKey = useStore((s) => s.jwtSecretKey);
  const jwtAutoGenerate = useStore((s) => s.jwtAutoGenerate);
  const jwtExpiration = useStore((s) => s.jwtExpiration);
  const javaVersions = useStore((s) => s.javaVersions);
  const springBootVersions = useStore((s) => s.springBootVersions);

  const languageSelected = language !== null;

  // Fetch versions on mount
  useEffect(() => {
    fetchJavaVersions().then((v) => {
      set('javaVersions', v);
      if (!javaVersion && v.length) set('javaVersion', v[0]);
    });
    fetchSpringBootVersions().then((v) => {
      set('springBootVersions', v);
      if (!springBootVersion && v.length) set('springBootVersion', v[0]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(id);
  }, [toast]);

  const callGenerate = useCallback(async () => {
    const json = generateJSON();
    if (!json.entities || json.entities.length === 0) {
      setToast(t('toolbar.noEntities'));
      return;
    }
    const token = useStore.getState().accessToken;
    setGenerating(true);
    setGenStatus(null);

    try {
      const res = await fetch(`${API_BASE}/api/v1/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(json),
      });

      const data = await res.json();

      if (data.success) {
        // Update projectId in store after successful generate
        if (data.data?.id) {
          set('projectId', data.data.id);
        }
        setGenStatus({
          type: 'success',
          message: data.message || 'Project generated!',
          downloadUrl: data.data?.downloadUrl,
          projectId: data.data?.id,
          expiredAt: data.data?.expiredAt,
        });
      } else {
        setGenStatus({ type: 'error', message: data.message || 'Generation failed' });
      }
    } catch {
      setGenStatus({ type: 'error', message: t('error.connection') });
    } finally {
      setGenerating(false);
    }
  }, [generateJSON, logout, t]);

  const handleGenerate = useCallback(() => {
    const json = generateJSON();
    if (!json.entities || json.entities.length === 0) {
      setToast(t('toolbar.noEntities'));
      return;
    }
    if (!accessToken) {
      setShowAuth(true);
      return;
    }
    callGenerate();
  }, [accessToken, callGenerate, generateJSON, t]);

  const handleAuthSuccess = useCallback(() => {
    setShowAuth(false);
    callGenerate();
  }, [callGenerate]);

  const handleDownload = useCallback(() => {
    if (!genStatus?.downloadUrl) return;
    const token = useStore.getState().accessToken;
    const url = `${API_BASE}${genStatus.downloadUrl}`;
    fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${useStore.getState().projectName}.zip`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  }, [genStatus]);

  const handlePreview = () => {
    const json = generateJSON();
    const newWindow = window.open('', '_blank');
    newWindow.document.write(
      `<pre style="background:#1e1e1e;color:#d4d4d4;padding:20px;margin:0;min-height:100vh;font-size:14px;">${JSON.stringify(json, null, 2)}</pre>`
    );
  };

  if (collapsed) {
    return (
      <div className="side-menu collapsed">
        <button className="menu-expand-btn" onClick={onToggle} title="Open menu">
          <span className="expand-icon">&#9776;</span>
        </button>
      </div>
    );
  }

  return (
    <div className={`side-menu${readOnly ? ' read-only' : ''}`}>
      {toast && (
        <div className="editor-toast" role="status">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="9" cy="9" r="7.5" />
            <path d="M9 5v4" />
            <circle cx="9" cy="12.5" r="0.8" fill="currentColor" />
          </svg>
          <span>{toast}</span>
        </div>
      )}
      {showAuth && (
        <AuthModal
          onSuccess={handleAuthSuccess}
          onCancel={() => setShowAuth(false)}
        />
      )}
      <div className="menu-header">
        <h3 className="menu-brand-link" onClick={() => window.location.href = '/home'}><Logo size={22} /></h3>
        <div className="menu-header-actions">
          <button className="menu-icon-btn" onClick={toggleTheme} title="Toggle theme">
            {theme === 'light' ? '\u263E' : '\u2600'}
          </button>
          <button className="menu-icon-btn" onClick={onToggle} title="Collapse">
            &#10094;
          </button>
        </div>
      </div>

      {/* Project */}
      <Section title={t('toolbar.project')} icon="&#128209;" defaultOpen>
        <div className="menu-field">
          <label>{t('toolbar.projectName')}</label>
          <input
            value={projectName}
            onChange={(e) => set('projectName', e.target.value)}
            placeholder="demo-project"
          />
        </div>
        <div className="menu-field">
          <label>{t('toolbar.basePackage')}</label>
          <input
            value={basePackage}
            onChange={(e) => set('basePackage', e.target.value)}
            placeholder="com.example"
          />
        </div>
        <div className="menu-field">
          <label>{t('toolbar.language')}</label>
          <select
            value={language || ''}
            onChange={(e) => setLanguage(e.target.value)}
            className={!languageSelected ? 'select-required' : ''}
          >
            <option value="" disabled>{t('toolbar.selectLangOption')}</option>
            <option value="java">Java</option>
          </select>
        </div>
        {language === 'java' && (
          <>
            <div className="menu-field">
              <label>{t('toolbar.buildTool')}</label>
              <select value={buildTool} onChange={(e) => set('buildTool', e.target.value)}>
                <option value="maven">Maven</option>
                <option value="gradle">Gradle</option>
              </select>
            </div>
            <div className="menu-field">
              <label>{t('toolbar.javaVersion')}</label>
              <select value={javaVersion} onChange={(e) => set('javaVersion', e.target.value)}>
                {javaVersions.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="menu-field">
              <label>{t('toolbar.springVersion')}</label>
              <select value={springBootVersion} onChange={(e) => set('springBootVersion', e.target.value)}>
                {springBootVersions.length === 0 && <option value="">Loading...</option>}
                {springBootVersions.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="menu-field">
              <label>{t('toolbar.packaging')}</label>
              <select value={packaging} onChange={(e) => set('packaging', e.target.value)}>
                <option value="jar">Jar</option>
                <option value="war">War</option>
              </select>
            </div>
          </>
        )}
      </Section>

      {/* Features */}
      <Section title={t('toolbar.features')} icon="&#9881;">
        <div className="menu-features">
          {Object.keys(features).map((key) => {
            const locked = ['dto', 'service', 'controller'].includes(key);
            const needsSecurity = key === 'cors';
            const isDisabled = locked || (needsSecurity && !features.security);
            return (
              <label key={key} className={`menu-feature-item${locked ? ' locked' : ''}${isDisabled && !locked ? ' disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={features[key]}
                  onChange={() => !isDisabled && toggleFeature(key)}
                  disabled={isDisabled}
                />
                <span>{key}</span>
              </label>
            );
          })}
        </div>
      </Section>

      {/* Database */}
      <Section title={t('toolbar.database')} icon="&#128451;">
        <div className="menu-field">
          <label>{t('toolbar.dbType')}</label>
          <select value={database} onChange={(e) => set('database', e.target.value)} disabled>
            <option value="postgresql">PostgreSQL</option>
          </select>
        </div>
        <div className="menu-field">
          <label>{t('toolbar.username')}</label>
          <input
            value={dbUsername}
            onChange={(e) => set('dbUsername', e.target.value)}
            placeholder="postgres"
          />
        </div>
        <div className="menu-field">
          <span className="auto-gen-hint">{t('toolbar.dbPasswordAutoGen')}</span>
        </div>
      </Section>

      {/* Security */}
      {features.security && (
        <Section title={t('toolbar.security')} icon="&#128274;">
          <div className="menu-field">
            <label>{t('toolbar.jwtSecret')}</label>
            <div className="input-with-btn">
              <input
                value={jwtSecretKey}
                onChange={(e) => {
                  set('jwtSecretKey', e.target.value);
                  set('jwtAutoGenerate', false);
                }}
                placeholder="your-secret-key"
              />
              <button
                className="auto-gen-btn"
                onClick={() => {
                  set('jwtSecretKey', '');
                  set('jwtAutoGenerate', true);
                }}
                title="Auto-generate on backend"
              >
                Auto
              </button>
            </div>
            {jwtAutoGenerate && (
              <span className="auto-gen-hint">Backend will auto-generate</span>
            )}
          </div>
          <div className="menu-field">
            <label>{t('toolbar.jwtExpiration')}</label>
            <input
              value={jwtExpiration}
              onChange={(e) => set('jwtExpiration', e.target.value)}
              placeholder="86400000"
            />
          </div>
        </Section>
      )}

      {/* Actions */}
      {!readOnly && !templateMode && (
        <div className="menu-section menu-actions">
          <button
            className="menu-action-btn generate"
            onClick={handleGenerate}
            disabled={!languageSelected || generating}
          >
            {generating ? t('toolbar.generating') : t('toolbar.generate')}
          </button>
        </div>
      )}

      {/* Success / Error Modal */}
      {genStatus && (
        <div className="gen-overlay" onClick={() => setGenStatus(null)}>
          <div className={`gen-modal ${genStatus.type}`} onClick={(e) => e.stopPropagation()}>
            {genStatus.type === 'success' ? (
              <>
                <div className="gen-modal-icon success-icon">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <circle cx="32" cy="32" r="30" stroke="#2ea043" strokeWidth="3" fill="rgba(46,160,67,0.1)" />
                    <path d="M20 33 L28 41 L44 23" stroke="#2ea043" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </div>
                <h2 className="gen-modal-title">{t('gen.success')}</h2>
                <p className="gen-modal-desc">{genStatus.message}</p>
                {genStatus.downloadUrl && (
                  <button className="gen-download-btn" onClick={handleDownload}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M9 2v10M5 8l4 4 4-4M3 14h12" />
                    </svg>
                    {t('gen.downloadZip')}
                  </button>
                )}
                {genStatus.expiredAt && (
                  <span className="gen-expire">
                    {t('gen.expires')}: {new Date(genStatus.expiredAt).toLocaleDateString()}
                  </span>
                )}
                <button className="gen-close-btn" onClick={() => setGenStatus(null)}>{t('gen.close')}</button>
              </>
            ) : (
              <>
                <div className="gen-modal-icon error-icon">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <circle cx="32" cy="32" r="30" stroke="#f85149" strokeWidth="3" fill="rgba(248,81,73,0.1)" />
                    <path d="M22 22 L42 42 M42 22 L22 42" stroke="#f85149" strokeWidth="3.5" strokeLinecap="round" fill="none" />
                  </svg>
                </div>
                <h2 className="gen-modal-title error-title">{t('gen.failed')}</h2>
                <p className="gen-modal-desc">{genStatus.message}</p>
                <button className="gen-close-btn" onClick={() => setGenStatus(null)}>{t('gen.tryAgain')}</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
